import { ClipboardCopyIcon } from '@radix-ui/react-icons';
import { Box, Flex, Grid, Heading, IconButton, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonDateInput } from 'components/common/form/date';
import { CommonSelectInput } from 'components/common/form/select';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { CommonTableHoC } from 'components/common/table';
import { HwDiagnosticDetailsHoC } from 'components/sections/admin-portal/machines/dialogs/settings/machine-hardware-diagnostics/details';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { isSameDay, parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { TABLES } from 'enums/tables';
import { t } from 'i18next';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSelectable } from 'interfaces/tables/selection';
import { ITableSortable } from 'interfaces/tables/sorting';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { DataCollectionType } from 'lib_ts/enums/data-collection.enums';
import { LOCAL_PRETTY_DATETIME_FORMAT } from 'lib_ts/enums/datetime.enums';
import { BallType } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IDataCollection } from 'lib_ts/interfaces/i-data-collection';
import { useEffect, useMemo, useState } from 'react';
import { DataCollectionsService } from 'services/data-collections.service';

const PAGE_SIZES = TABLES.PAGE_SIZES.SM;

interface IMachineHwDiagnosticsFilters {
  ball_type?: BallType;
  startDate?: Date;
}

const columns: ITypedTableColumn<IDataCollection>[] = [
  {
    label: 'common.start-date',
    key: '_created',
    dataType: 'datetime',
    formatFn: (m) => format(parseISO(m._created), LOCAL_PRETTY_DATETIME_FORMAT),
  },
  {
    label: 'settings.user',
    key: 'user',
  },
  {
    label: 'common.ball-type',
    key: 'ball_type',
  },
  {
    label: 'Reference ID',
    labelTooltip: 'AKA: Collection ID',
    key: 'collectionID',
    formatFn: (m) => (
      <Flex gap="1" align="center">
        <Text className="font-mono">{m.collectionID}</Text>
        <IconButton
          className="btn-floating"
          color={RADIX.COLOR.NEUTRAL}
          variant={RADIX.BUTTON.VARIANT.BORDERLESS}
          onClick={(e) => {
            e.stopPropagation();

            navigator.clipboard.writeText(m.collectionID);

            NotifyHelper.success({
              message_md: t('common.x-copied-to-clipboard', {
                x: `\`${m.collectionID}\``,
              }),
            });
          }}
        >
          <ClipboardCopyIcon />
        </IconButton>
      </Flex>
    ),
  },
];

export const MachineHardwareDiagnostics = () => {
  const machine = useMachinesPortalStore((s) => s.machine);

  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState<IDataCollection[]>([]);
  const [selectedSession, setSelectedSession] = useState<IDataCollection>();
  const [filter, setFilter] = useState<IMachineHwDiagnosticsFilters>({
    startDate: undefined,
    ball_type: undefined,
  });

  useEffect(() => {
    if (!machine?.machineID) {
      return;
    }

    const fetchMachineHwDiagnostics = async () => {
      try {
        setLoading(true);
        const result = await DataCollectionsService.search({
          machineID: machine.machineID,
          type: DataCollectionType.HardwareDiagnostic,
          complete: true,
        });
        setSessions(result);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchMachineHwDiagnostics();
  }, [machine?.machineID]);

  const applyFilter = (v: Partial<IMachineHwDiagnosticsFilters>) => {
    setFilter((prev) => ({
      ...prev,
      ...v,
    }));
  };

  const filtered = useMemo(() => {
    return sessions
      .filter((m) => {
        if (!filter.startDate) {
          return true;
        }

        return isSameDay(filter.startDate, parseISO(m._created));
      })
      .filter((m) => {
        if (!filter.ball_type) {
          return true;
        }
        return filter.ball_type === m.ball_type;
      });
  }, [sessions, filter]);

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: '_created',
      dir: 1,
    },
  };

  const select: ITableSelectable = {
    enableSelect: true,
    afterChangeSelected: (model?) => setSelectedSession(model),
  };

  const pagination: ITablePageable = {
    paginationID: PaginationID.AdminMachineHwDiagnostics,
    total: filtered.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  return (
    <ErrorBoundary identifier="MachineHwDiagnostics">
      <Flex
        direction="column"
        gap={RADIX.FLEX.GAP.MD}
        className={RADIX.VFLEX.GROW}
      >
        {selectedSession && machine && (
          <HwDiagnosticDetailsHoC
            hwd={selectedSession}
            onBack={() => setSelectedSession(undefined)}
            machine={machine}
          />
        )}

        <Flex
          direction="column"
          gap={RADIX.FLEX.GAP.MD}
          className={StringHelper.classNames([
            RADIX.VFLEX.GROW,
            /* always keep table in the DOM to persist input states */
            selectedSession ? 'hidden' : '',
          ])}
        >
          <Flex align="center" gap={RADIX.FLEX.GAP.SM}>
            <Heading size="4">{t('common.hardware-diagnostics')} </Heading>
            <SuperAdminIcon />
          </Flex>

          <FlexTableWrapper
            gap={RADIX.FLEX.GAP.FORM}
            header={
              <Grid columns="4" gap={RADIX.FLEX.GAP.SM}>
                <Box gridColumn="span 1">
                  <CommonDateInput
                    id="machine-hwd-start-date-filter"
                    placeholder="common.start-date"
                    onChange={(v) => applyFilter({ startDate: v })}
                  />
                </Box>
                <Box gridColumn="span 1">
                  <CommonSelectInput
                    id="machine-hwd-ball-type-filter"
                    placeholder="common.ball-type"
                    options={Object.values(BallType).map((o) => ({
                      label: o,
                      value: o,
                      invisible: !machine?.ball_type_options.includes(o),
                    }))}
                    onChange={(v) => applyFilter({ ball_type: v as BallType })}
                  />
                </Box>
              </Grid>
            }
            table={
              <CommonTableHoC
                dataTestID="HwDiagnostics"
                identifier="HwDiagnosticsTable"
                displayColumns={columns}
                displayData={filtered}
                loading={loading}
                {...pagination}
                {...select}
                {...sort}
                vFlex
              />
            }
          />
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};
